/**
 * Admin API service — calls /v1/admin/* endpoints.
 *
 * Supports: developer onboarding (with Cognito reconnect), developer CRUD,
 * API key management, user management (CRUD, status, search, delete),
 * token credit, usage logs, system metrics.
 */

import { api } from './api.client';

// ── Types ────────────────────────────────────────────────────────

export interface AdminDeveloper {
  developerId: string;
  email: string;
  name: string;
  namespace: string;
  tier: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  apiKeyCount?: number;
}

export interface AdminMetrics {
  totalDevelopers: number;
  totalUsers: number;
  totalApiKeys: number;
  totalUserTokens: number;
}

export interface DeveloperListResult {
  developers: AdminDeveloper[];
  nextCursor?: string;
}

export interface AdminApiKey {
  id: string;
  keyPrefix: string;
  name: string;
  environment: string;
  status: string;
  requestsPerMinute: number;
  requestsPerDay: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface OnboardResult {
  developer: Record<string, unknown>;
  cognitoUsername: string;
  apiKey: {
    id: string;
    keyPrefix: string;
    fullKey: string;
    environment: string;
  };
  message: string;
}

export interface AdminUser {
  userId: string;
  email: string;
  fullName: string;
  status: string;
  createdAt: string;
}

export interface AdminUserToken {
  tokenId: string;
  tokenPrefix: string;
  status: string;
  balance: number;
  dailyLimit: number | null;
  monthlyLimit: number | null;
  perRequestLimit: number | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface AdminUserDetail extends AdminUser {
  tokens: AdminUserToken[];
}

export interface UserListResult {
  users: AdminUser[];
  nextCursor?: string;
}

export interface UsageLogItem {
  developerId: string;
  requestId: string;
  model: string;
  tokensDeducted: number;
  latencyMs: number;
  userId: string;
  userTokenId: string;
  error: string | null;
  timestamp: string;
}

export interface UsageLogResult {
  items: UsageLogItem[];
  nextCursor?: string;
}

// ── Developer Onboarding ─────────────────────────────────────────

export async function onboardDeveloper(data: {
  email: string;
  name: string;
  namespace?: string;
  tier?: string;
}): Promise<OnboardResult> {
  return api.post<OnboardResult>('/admin/developers/onboard', data);
}

// ── Developer CRUD ───────────────────────────────────────────────

export async function listDevelopers(
  limit: number = 25,
  cursor?: string
): Promise<DeveloperListResult> {
  const query: Record<string, string> = { limit: String(limit) };
  if (cursor) query.cursor = cursor;
  return api.get<DeveloperListResult>('/admin/developers', query);
}

export async function getDeveloper(id: string): Promise<AdminDeveloper> {
  return api.get<AdminDeveloper>(`/admin/developers/${id}`);
}

export async function updateDeveloper(
  id: string,
  data: Partial<{ name: string; namespace: string; tier: string; status: string }>
): Promise<AdminDeveloper> {
  return api.patch<AdminDeveloper>(`/admin/developers/${id}`, data);
}

export async function deleteDeveloper(
  id: string,
  fundsRepaid: boolean
): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/developers/${id}`, { fundsRepaid });
}

// ── Developer API Keys ───────────────────────────────────────────

export async function listDeveloperApiKeys(
  developerId: string
): Promise<{ apiKeys: AdminApiKey[] }> {
  return api.get<{ apiKeys: AdminApiKey[] }>(
    `/admin/developers/${developerId}/api-keys`
  );
}

export async function createDeveloperApiKey(
  developerId: string,
  data: { name: string; environment?: string }
): Promise<{ id: string; keyPrefix: string; fullKey: string; environment: string }> {
  return api.post(`/admin/developers/${developerId}/api-keys`, data);
}

export async function revokeDeveloperApiKey(
  developerId: string,
  keyId: string
): Promise<void> {
  await api.delete(`/admin/developers/${developerId}/api-keys/${keyId}`);
}

// ── User Management ──────────────────────────────────────────────

export async function listUsers(
  limit: number = 25,
  cursor?: string
): Promise<UserListResult> {
  const query: Record<string, string> = { limit: String(limit) };
  if (cursor) query.cursor = cursor;
  return api.get<UserListResult>('/admin/users', query);
}

export async function getUserDetail(id: string): Promise<AdminUserDetail> {
  return api.get<AdminUserDetail>(`/admin/users/${id}`);
}

export async function creditTokens(data: {
  tokenId: string;
  amount: number;
  reason: string;
}): Promise<{ newBalance: number }> {
  return api.post<{ newBalance: number }>('/admin/users/tokens/credit', data);
}

export async function updateUser(
  id: string,
  data: Partial<{ fullName: string; status: string }>
): Promise<AdminUser> {
  return api.patch<AdminUser>(`/admin/users/${id}`, data);
}

export async function deleteUser(
  id: string,
  fundsRepaid: boolean
): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/admin/users/${id}`, { fundsRepaid });
}

export async function searchUserByEmail(
  email: string
): Promise<AdminUser> {
  return api.get<AdminUser>('/admin/users/search', { email });
}

// ── Usage Logs ───────────────────────────────────────────────────

export async function getUsageLogs(options?: {
  developerId?: string;
  limit?: number;
  cursor?: string;
}): Promise<UsageLogResult> {
  const query: Record<string, string> = {};
  if (options?.developerId) query.developerId = options.developerId;
  if (options?.limit) query.limit = String(options.limit);
  if (options?.cursor) query.cursor = options.cursor;
  return api.get<UsageLogResult>('/admin/usage-logs', query);
}

// ── System Metrics ───────────────────────────────────────────────

export async function getSystemMetrics(): Promise<AdminMetrics> {
  return api.get<AdminMetrics>('/admin/metrics');
}
